import {
  Component,
  Input,
  signal,
  Optional,
  Self,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormsModule,
  ReactiveFormsModule,
  NgControl
} from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-form-date',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DatePickerModule],
  template: `
    <div class="input">
      @if (label) {
        <label [for]="id" class="input-label-item">{{ label }}</label>
      }
      <p-datepicker
        [inputId]="id"
        [showIcon]="true"
        [showOnFocus]="false"
        [showButtonBar]="true"
        [keepInvalid]="true"
        [minDate]="minDate"
        [maxDate]="maxDate"
        [disabled]="disabled"
        dateFormat="dd/mm/yy"
        [placeholder]="placeholder"
        styleClass="w-full"
        inputStyleClass="!border-blue-deep hover:!border-blue-deep focus:!border-blue-deep !text-blue-deep !rounded-xl !py-[10px] !px-[10px] !h-auto"
        appendTo="body"
        [(ngModel)]="currentValue"
        (ngModelChange)="onValueChange($event)"
        (onBlur)="onInputBlur($event)"
      />
      @if (showError()) {
        <div class="input-error mt-1">
          @if (errorType() === 'required') {
            Este campo es requerido
          } @else if (errorType() === 'maxDate') {
            {{ maxDateMessage() }}
          } @else {
            Fecha inválida
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      ::ng-deep .p-datepicker-input-icon-container {
        top: 50% !important;
        transform: translateY(-50%) !important;
        color: #0033a0 !important;
      }

      ::ng-deep .p-inputtext {
        color: #0033a0 !important;
        font-family: 'Sura-Regular', sans-serif !important;
      }

      ::ng-deep .p-inputtext::placeholder {
        color: #9ca3af !important;
      }

      ::ng-deep .p-datepicker-overlay,
      ::ng-deep .p-overlay,
      ::ng-deep .p-datepicker-panel,
      ::ng-deep .p-datepicker {
        z-index: 10000 !important;
      }
    `
  ]
})
export class FormDateComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() id = 'dateInput';
  @Input() placeholder = 'Seleccione fecha';
  @Input() minDate?: Date;
  @Input() maxDate?: Date;

  readonly showError = signal(false);
  readonly errorType = signal<string>('');
  readonly maxDateMessage = signal<string>('La fecha no puede ser futura');
  currentValue: Date | null = null;
  disabled = false;

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      this.updateErrorState();
    });
  }

  private updateErrorState(): void {
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }
    const hasError = control.invalid && (control.touched || control.dirty);
    this.showError.set(hasError);
    if (hasError && control.errors) {
      this.resolveErrorType(control.errors);
    } else {
      this.errorType.set('');
    }
  }

  private resolveErrorType(errors: Record<string, unknown>): void {
    if (errors['required']) {
      this.errorType.set('required');
    } else if (errors['maxDate']) {
      this.errorType.set('maxDate');
      const maxDateErr = errors['maxDate'] as { message?: string };
      this.maxDateMessage.set(maxDateErr?.message || 'La fecha no puede ser futura');
    } else {
      this.errorType.set('other');
    }
  }

  /**
   * Handles the value change from the datepicker.
   * If PrimeNG passes a string (from manual input), parse it to Date.
   */
  onValueChange(value: Date | string | null): void {
    if (!this.disabled) {
      const parsed = this.parseToDate(value);
      this.currentValue = parsed;
      this.onChange(parsed);
      this.onTouched();
    }
  }

  /**
   * Handles blur on the input to catch manually typed dates
   * that PrimeNG may not have parsed (e.g. "2025-03-03").
   */
  onInputBlur(event: unknown): void {
    this.onTouched();
    const inputEl = (event as { target?: HTMLInputElement })?.target;
    if (!inputEl) {
      return;
    }

    const rawValue = inputEl.value?.trim();
    if (!rawValue) {
      return;
    }

    // If currentValue is already a valid Date, nothing to do
    if (this.currentValue instanceof Date && !isNaN(this.currentValue.getTime())) {
      return;
    }

    // Try to parse the manually entered text
    const parsed = this.parseDateString(rawValue);
    if (parsed) {
      this.currentValue = parsed;
      this.onChange(parsed);
    }
  }

  writeValue(value: Date | string | null): void {
    this.currentValue = this.parseToDate(value);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Converts a value (Date, string, or null) into a Date or null.
   * Handles ISO formats like "2025-03-03" and "2025-03-03T00:00:00".
   */
  private parseToDate(value: Date | string | null): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    return this.parseDateString(value);
  }

  /**
   * Parses a date string in common formats (yyyy-MM-dd, dd/MM/yyyy, etc.)
   * and returns a Date object or null if unparseable.
   */
  private parseDateString(text: string): Date | null {
    if (!text) {
      return null;
    }

    // Try ISO format: yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss
    const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10) - 1;
      const day = parseInt(isoMatch[3], 10);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month && d.getDate() === day) {
        return d;
      }
    }

    // Try dd/MM/yyyy format
    const slashMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const day = parseInt(slashMatch[1], 10);
      const month = parseInt(slashMatch[2], 10) - 1;
      const year = parseInt(slashMatch[3], 10);
      const d = new Date(year, month, day);
      if (!isNaN(d.getTime()) && d.getFullYear() === year && d.getMonth() === month && d.getDate() === day) {
        return d;
      }
    }

    return null;
  }
}
